import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchDataFromApi, postData } from "../../utils/api";

// Async thunks
export const fetchCartItems = createAsyncThunk(
  "cart/fetchCartItems",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetchDataFromApi(`/api/cart?userId=${userId}`);
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch cart items");
    }
  }
);

export const addItemToCart = createAsyncThunk(
  "cart/addItemToCart",
  async (cartData, { rejectWithValue }) => {
    try {
      const response = await postData("/api/cart/add", cartData);
      console.log(response, "response ");
      if (response.success === false) {
        return rejectWithValue(
          response.message || "Failed to add item to cart"
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add item to cart");
    }
  }
);

export const updateCartItemQuantity = createAsyncThunk(
  "cart/updateCartItemQuantity",
  async ({ itemId, quantity }, { rejectWithValue }) => {
    try {
      const response = await postData("/api/cart/update", { itemId, quantity });
      if (response.status === false) {
        return rejectWithValue(response.msg || "Failed to update cart item");
      }

      // Fetch updated cart after updating item
      const user = JSON.parse(localStorage.getItem("user"));
      const updatedCart = await fetchDataFromApi(
        `/api/cart?userId=${user._id}`
      );
      return updatedCart.items || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update cart item");
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await postData("/api/cart/remove", { itemId });
      if (response.status === false) {
        return rejectWithValue(response.msg || "Failed to remove cart item");
      }

      // Fetch updated cart after removing item
      const user = JSON.parse(localStorage.getItem("user"));
      const updatedCart = await fetchDataFromApi(
        `/api/cart?userId=${user._id}`
      );
      return updatedCart.items || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to remove cart item");
    }
  }
);

const initialState = {
  cartItems: [],
  loading: false,
  error: null,
  total: 0,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartItems: (state, action) => {
      state.cartItems = action.payload;
      state.total = calculateTotal(action.payload);
      state.loading = false;
      state.error = null;
    },
    addToCart: (state, action) => {
      const existingItem = state.cartItems.find(
        (item) => item._id === action.payload._id
      );
      if (existingItem) {
        existingItem.quantity = action.payload.quantity;
        existingItem.subTotal = action.payload.subTotal;
      } else {
        state.cartItems.push(action.payload);
      }
      state.total = calculateTotal(state.cartItems);
      state.loading = false;
      state.error = null;
    },
    updateCartItem: (state, action) => {
      const { _id, quantity, subTotal } = action.payload;
      const item = state.cartItems.find((item) => item._id === _id);
      if (item) {
        item.quantity = quantity;
        item.subTotal = subTotal;
      }
      state.total = calculateTotal(state.cartItems);
      state.loading = false;
      state.error = null;
    },
    removeFromCart: (state, action) => {
      state.cartItems = state.cartItems.filter(
        (item) => item._id !== action.payload
      );
      state.total = calculateTotal(state.cartItems);
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearCart: (state) => {
      state.cartItems = [];
      state.total = 0;
      state.loading = false;
      state.error = null;
    },
    updateCartTotal: (state) => {
      state.total = calculateTotal(state.cartItems);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart items
      .addCase(fetchCartItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.cartItems = action.payload;
        state.total = calculateTotal(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add item to cart
      .addCase(addItemToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemToCart.fulfilled, (state, action) => {
        const newItem = action.payload;
        const existingItem = state.cartItems.find(
          (item) => item._id === newItem._id
        );

        if (!existingItem) {
          state.cartItems.push(newItem);
        }

        state.total = calculateTotal(state.cartItems);
        state.loading = false;
        state.error = null;
      })
      .addCase(addItemToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update cart item
      .addCase(updateCartItemQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
        state.cartItems = action.payload;
        state.total = calculateTotal(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove cart item
      .addCase(removeCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        state.cartItems = action.payload;
        state.total = calculateTotal(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Helper function
const calculateTotal = (items) => {
  return items.reduce((total, item) => total + (item.subTotal || 0), 0);
};

export const {
  setCartItems,
  addToCart,
  updateCartItem,
  removeFromCart,
  setLoading,
  setError,
  clearCart,
  updateCartTotal,
} = cartSlice.actions;

export default cartSlice.reducer;
